// The altitude scoping questions per item category, and how three answers
// become an altitude (1–5). Pure data and arithmetic: one copy for the browser
// (src/lib/altitudeQuestions.ts re-exports it) and the server classify runner
// (ambient/lib/classifyServer.ts).
//
// Altitude measures structural scope and complexity — not value or urgency.
// Each question set maps to three structural dimensions:
//
// - Reversibility (stored as timeHorizon): how locked in are we if we're wrong?
// - Coordination complexity (stored as workSize): how many groups must align?
// - Scope / blast radius (stored as impactSurface): how broadly does this reach?

// The same unions as src/types/prioritization (lifecycle.ts, leveling.ts,
// itemLifecycle.ts); an edge function cannot import from src.
export type ItemLanguageType = 'problem' | 'opportunity' | 'risk' | 'solution' | 'commitment' | 'investment';
export type TimeHorizon = 'days' | 'weeks' | '1-3_months' | '3-12_months' | '1+_years';
export type WorkSize = '1-2_weeks' | '3-8_weeks' | '9-26_weeks' | '27-52_weeks' | '52+_weeks';
export type ImpactSurface = 'one_screen' | 'one_workflow' | 'multiple_workflows' | 'multiple_products' | 'company_wide';
export type AltitudeLevel = 1 | 2 | 3 | 4 | 5;

/**
 * A single altitude question with response options
 */
export interface AltitudeQuestion {
  id: string;
  dimension: 'timeHorizon' | 'workSize' | 'impactSurface';
  question: string;
  helpText?: string;
  options: AltitudeQuestionOption[];
}

/**
 * An option for an altitude question (maps to 1-5 score)
 */
export interface AltitudeQuestionOption {
  value: 1 | 2 | 3 | 4 | 5;
  label: string;
  description: string;
  mappedValue: TimeHorizon | WorkSize | ImpactSurface;
}

/**
 * Complete question set for a candidate type
 */
export interface AltitudeQuestionSet {
  candidateType: ItemLanguageType;
  displayName: string;
  description: string;
  questions: AltitudeQuestion[];
}

// ============================================================================
// Reversibility Questions by Candidate Type
// (stored as dimension: 'timeHorizon' — measures lock-in, not value timing)
// ============================================================================

export const TIME_HORIZON_BASE_OPTIONS: Omit<AltitudeQuestionOption, 'label' | 'description'>[] = [
  { value: 1, mappedValue: 'days' },
  { value: 2, mappedValue: 'weeks' },
  { value: 3, mappedValue: '1-3_months' },
  { value: 4, mappedValue: '3-12_months' },
  { value: 5, mappedValue: '1+_years' },
];

export const TIME_HORIZON_QUESTIONS: Record<ItemLanguageType, AltitudeQuestion> = {
  problem: {
    id: 'problem_time_horizon',
    dimension: 'timeHorizon',
    question: 'If we address this problem incorrectly, how easy is it to course correct?',
    helpText: 'Consider the cost and complexity of reversing course if the first approach is wrong',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'Easily Revised', description: 'We can try a different approach quickly with minimal cost' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Low Switching Cost', description: 'Some rework required, but easy to change direction' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Moderate Lock-In', description: 'Meaningful effort required to reverse course' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'Hard to Undo', description: 'Significant investment locked in; changing direction is costly' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Near-Irreversible', description: 'Commits the organization to a direction for years' },
    ],
  },
  opportunity: {
    id: 'opportunity_time_horizon',
    dimension: 'timeHorizon',
    question: 'If we pursue this and it doesn\'t pan out, how easy is it to pull back?',
    helpText: 'Consider exit costs, capital committed, and how locked in the organization becomes',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'No Lock-In', description: 'Easy to stop and redirect with minimal loss' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Low Switching Cost', description: 'Minor rework or write-offs to exit' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Moderate Exit Cost', description: 'Meaningful cost to change direction' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'Hard to Exit', description: 'Significant investment is difficult to unwind' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Near-Irreversible', description: 'Strategic commitment that is hard to reverse' },
    ],
  },
  solution: {
    id: 'solution_time_horizon',
    dimension: 'timeHorizon',
    question: 'If this solution turns out to be wrong, how hard is it to change course?',
    helpText: 'Consider architectural coupling, migration cost, and downstream dependencies',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'Easily Replaced', description: 'Simple to swap out or refactor' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Low Switching Cost', description: 'Minor rework to replace or alter' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Moderate Coupling', description: 'Meaningful effort to reverse or redesign' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'Deep Coupling', description: 'Hard to reverse; significant downstream impact' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Near-Irreversible', description: 'Architectural commitment that locks in direction' },
    ],
  },
  risk: {
    id: 'risk_time_horizon',
    dimension: 'timeHorizon',
    question: 'If this risk materializes, how recoverable is the damage?',
    helpText: 'Consider how quickly and completely the organization could bounce back',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'Fully Recoverable', description: 'Quick bounce-back, minimal lasting impact' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Mostly Recoverable', description: 'Some lasting impact, but manageable recovery' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Partial Recovery', description: 'Significant recovery effort required' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'Hard to Recover', description: 'Lasting damage to operations, relationships, or reputation' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Catastrophic', description: 'Irreversible or existential impact' },
    ],
  },
  investment: {
    id: 'investment_time_horizon',
    dimension: 'timeHorizon',
    question: 'If this investment underperforms, how easy is it to redirect the resources?',
    helpText: 'Consider capital committed, contracts, and organizational lock-in',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'Easily Redirected', description: 'Minimal lock-in; resources are fluid' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Low Exit Cost', description: 'Minor losses to pull back' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Moderate Lock-In', description: 'Some capital or commitments hard to exit' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'Hard to Exit', description: 'Significant capital or contracts committed' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Near-Irreversible', description: 'Major long-term financial commitment' },
    ],
  },
  commitment: {
    id: 'commitment_time_horizon',
    dimension: 'timeHorizon',
    question: 'If we need to renegotiate or exit this commitment, how serious are the consequences?',
    helpText: 'Consider relationship damage, legal exposure, and reputational risk of not delivering',
    options: [
      { ...TIME_HORIZON_BASE_OPTIONS[0], label: 'Low Stakes', description: 'Easy to renegotiate; minimal relationship cost' },
      { ...TIME_HORIZON_BASE_OPTIONS[1], label: 'Minor Consequences', description: 'Some trust or goodwill cost to change' },
      { ...TIME_HORIZON_BASE_OPTIONS[2], label: 'Moderate Stakes', description: 'Meaningful relationship or contractual cost' },
      { ...TIME_HORIZON_BASE_OPTIONS[3], label: 'High Stakes', description: 'Significant legal, financial, or relationship consequences' },
      { ...TIME_HORIZON_BASE_OPTIONS[4], label: 'Binding', description: 'Irreversible or legally/reputationally locked in' },
    ],
  },
};

// ============================================================================
// Coordination Complexity Questions by Candidate Type
// (stored as dimension: 'workSize' — measures how many groups must align)
// ============================================================================

export const WORK_SIZE_BASE_OPTIONS: Omit<AltitudeQuestionOption, 'label' | 'description'>[] = [
  { value: 1, mappedValue: '1-2_weeks' },
  { value: 2, mappedValue: '3-8_weeks' },
  { value: 3, mappedValue: '9-26_weeks' },
  { value: 4, mappedValue: '27-52_weeks' },
  { value: 5, mappedValue: '52+_weeks' },
];

export const WORK_SIZE_QUESTIONS: Record<ItemLanguageType, AltitudeQuestion> = {
  problem: {
    id: 'problem_work_size',
    dimension: 'workSize',
    question: 'How many teams or groups need to coordinate to resolve this problem?',
    helpText: 'Consider who needs to investigate, decide, implement, and verify',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Person', description: 'A single person or small team can handle it end to end' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'One Team', description: 'Contained within one team; no cross-team dependency' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Cross-Team', description: 'Two or three teams need to coordinate' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Cross-Functional', description: 'Multiple departments or functions must align' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide', description: 'Leadership or company-wide coordination required' },
    ],
  },
  opportunity: {
    id: 'opportunity_work_size',
    dimension: 'workSize',
    question: 'How many teams need to align to capture this opportunity?',
    helpText: 'Consider research, development, go-to-market, and operational coordination',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Team', description: 'A single team can own and execute' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'Two Teams', description: 'One primary team with light input from another' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Cross-Team', description: 'Multiple teams need to actively coordinate' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Cross-Functional', description: 'Multiple departments must align' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide', description: 'Requires company-wide coordination or external partners' },
    ],
  },
  solution: {
    id: 'solution_work_size',
    dimension: 'workSize',
    question: 'How many teams need to coordinate to design, build, and ship this?',
    helpText: 'Include design, engineering, QA, ops, and any downstream owners',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Engineer / Team', description: 'Fully contained within one team' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'Two Teams', description: 'Primary team with light dependency on one other' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Three or More Teams', description: 'Active coordination across multiple teams' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Cross-Functional', description: 'Multiple departments or product areas must align' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide', description: 'Requires broad organizational coordination' },
    ],
  },
  risk: {
    id: 'risk_work_size',
    dimension: 'workSize',
    question: 'How many teams would need to coordinate to mitigate or respond to this risk?',
    helpText: 'Consider detection, response, recovery, and communication across the organization',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Team', description: 'One team can detect and respond independently' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'Two Teams', description: 'Light coordination with one other team' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Cross-Team', description: 'Multiple teams must coordinate to respond' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Cross-Functional', description: 'Organization-wide response coordination required' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide or External', description: 'Requires executive, board, or external coordination' },
    ],
  },
  investment: {
    id: 'investment_work_size',
    dimension: 'workSize',
    question: 'How many groups need to align to approve, manage, and execute this investment?',
    helpText: 'Consider budget owners, delivery teams, legal, and executive sign-off',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Team', description: 'Single team can own and execute' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'Two Groups', description: 'One primary owner with light dependency on another' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Cross-Functional', description: 'Multiple groups need active coordination' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Leadership Alignment', description: 'Executive or board involvement required' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide or External', description: 'Multi-organization or public coordination required' },
    ],
  },
  commitment: {
    id: 'commitment_work_size',
    dimension: 'workSize',
    question: 'How many internal teams need to coordinate to fulfill this commitment?',
    helpText: 'Consider who must deliver, who must support, and who must sign off',
    options: [
      { ...WORK_SIZE_BASE_OPTIONS[0], label: 'One Team', description: 'A single team owns delivery end to end' },
      { ...WORK_SIZE_BASE_OPTIONS[1], label: 'Two Teams', description: 'Primary team with light dependency on one other' },
      { ...WORK_SIZE_BASE_OPTIONS[2], label: 'Cross-Team', description: 'Multiple teams actively coordinating' },
      { ...WORK_SIZE_BASE_OPTIONS[3], label: 'Cross-Functional', description: 'Multiple departments must align to deliver' },
      { ...WORK_SIZE_BASE_OPTIONS[4], label: 'Org-Wide', description: 'Requires broad organizational coordination or executive sign-off' },
    ],
  },
};

// ============================================================================
// Scope / Blast Radius Questions by Candidate Type
// (stored as dimension: 'impactSurface' — measures breadth of reach/exposure)
// ============================================================================

export const IMPACT_SURFACE_BASE_OPTIONS: Omit<AltitudeQuestionOption, 'label' | 'description'>[] = [
  { value: 1, mappedValue: 'one_screen' },
  { value: 2, mappedValue: 'one_workflow' },
  { value: 3, mappedValue: 'multiple_workflows' },
  { value: 4, mappedValue: 'multiple_products' },
  { value: 5, mappedValue: 'company_wide' },
];

export const IMPACT_SURFACE_QUESTIONS: Record<ItemLanguageType, AltitudeQuestion> = {
  problem: {
    id: 'problem_impact_surface',
    dimension: 'impactSurface',
    question: 'How broadly does this problem affect the organization?',
    helpText: 'Consider how many people, teams, systems, or customers are impacted',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'Isolated', description: 'Affects one person, component, or corner case' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One Team / Workflow', description: 'Contained within one team or user journey' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Teams', description: 'Cross-cutting; affects several teams or workflows' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Multiple Products / Units', description: 'Systemic; affects multiple product areas or business units' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide', description: 'Organization-level or customer-facing at scale' },
    ],
  },
  opportunity: {
    id: 'opportunity_impact_surface',
    dimension: 'impactSurface',
    question: 'How broadly would capturing this opportunity affect the organization?',
    helpText: 'Consider how many teams, products, or markets would benefit',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'One Feature / Team', description: 'Narrow scope; one area benefits' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One Product Area', description: 'Single product or team impact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Areas', description: 'Cross-functional benefit' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Multiple Products', description: 'Portfolio-level impact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide', description: 'Strategic transformation across the organization' },
    ],
  },
  solution: {
    id: 'solution_impact_surface',
    dimension: 'impactSurface',
    question: 'How broadly does this solution reach across users and systems?',
    helpText: 'Consider downstream dependencies, data flows, and user populations affected',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'One Screen / Component', description: 'Isolated UI or system change' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One Workflow', description: 'Single user flow affected' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Workflows', description: 'Cross-cutting system change' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Multiple Products', description: 'Platform-level change' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide', description: 'Enterprise-wide architectural impact' },
    ],
  },
  risk: {
    id: 'risk_impact_surface',
    dimension: 'impactSurface',
    question: 'How broad is the blast radius if this risk materializes?',
    helpText: 'Consider how many teams, customers, or systems would be affected',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'One Component', description: 'Isolated, contained failure' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One System / Team', description: 'One system or team affected' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Systems', description: 'Cross-system exposure' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Multiple Products / Markets', description: 'Portfolio or market exposure' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide', description: 'Existential, reputational, or regulatory' },
    ],
  },
  investment: {
    id: 'investment_impact_surface',
    dimension: 'impactSurface',
    question: 'How broadly does this investment affect the organization?',
    helpText: 'Consider how many teams, products, or capabilities are touched',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'One Team', description: 'Team-level benefit or change' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One Department', description: 'Departmental impact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Departments', description: 'Cross-functional impact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Multiple Products', description: 'Portfolio-level impact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide', description: 'Enterprise transformation' },
    ],
  },
  commitment: {
    id: 'commitment_impact_surface',
    dimension: 'impactSurface',
    question: 'How many stakeholders — internal and external — does this commitment affect?',
    helpText: 'Consider the breadth of who is counting on this being delivered',
    options: [
      { ...IMPACT_SURFACE_BASE_OPTIONS[0], label: 'One Person / Contact', description: 'Individual or single point of contact' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[1], label: 'One Account', description: 'Single customer or partner' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[2], label: 'Multiple Accounts', description: 'Customer segment or several partners' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[3], label: 'Market / Partner Network', description: 'Public-facing or market-level commitment' },
      { ...IMPACT_SURFACE_BASE_OPTIONS[4], label: 'Company-Wide or Regulatory', description: 'Public, legal, or organization-wide obligation' },
    ],
  },
};

/**
 * Get the full question set for a candidate type
 */
export function getAltitudeQuestionSet(candidateType: ItemLanguageType): AltitudeQuestionSet {
  const displayNames: Record<ItemLanguageType, string> = {
    problem: 'Problem',
    opportunity: 'Opportunity',
    solution: 'Solution',
    risk: 'Risk',
    investment: 'Investment',
    commitment: 'Commitment',
  };
  
  const descriptions: Record<ItemLanguageType, string> = {
    problem: 'Something is wrong that needs fixing',
    opportunity: 'Something we could gain by taking action',
    solution: 'A specific approach or implementation',
    risk: 'Something that might go wrong',
    investment: 'A strategic bet for future value',
    commitment: 'An external promise we must keep',
  };
  
  return {
    candidateType,
    displayName: displayNames[candidateType],
    description: descriptions[candidateType],
    questions: [
      TIME_HORIZON_QUESTIONS[candidateType],
      WORK_SIZE_QUESTIONS[candidateType],
      IMPACT_SURFACE_QUESTIONS[candidateType],
    ],
  };
}

/**
 * Get all question sets (for admin/configuration purposes)
 */
export function getAllAltitudeQuestionSets(): AltitudeQuestionSet[] {
  const candidateTypes: ItemLanguageType[] = [
    'problem',
    'opportunity', 
    'solution',
    'risk',
    'investment',
    'commitment',
  ];
  
  return candidateTypes.map(getAltitudeQuestionSet);
}

// ============================================================================
// Response Processing
// ============================================================================

/**
 * Response to an altitude question
 */
export interface AltitudeQuestionResponse {
  questionId: string;
  dimension: 'timeHorizon' | 'workSize' | 'impactSurface';
  selectedValue: 1 | 2 | 3 | 4 | 5;
  mappedValue: TimeHorizon | WorkSize | ImpactSurface;
}

/**
 * Compute altitude level from question responses
 */
export function computeAltitudeFromResponses(
  responses: AltitudeQuestionResponse[]
): AltitudeLevel {
  if (responses.length === 0) {
    return 3; // Default to middle
  }
  
  const sum = responses.reduce((acc, r) => acc + r.selectedValue, 0);
  const avg = sum / responses.length;
  const rounded = Math.round(avg);
  
  return Math.max(1, Math.min(5, rounded)) as AltitudeLevel;
}
